import { Component } from "react";

import type { IComponent } from "@Components/Component";
import { defaultAttributes } from "@Components/Component";
import type { ILink } from "@Components/Link";
import { LinkAppearance, LinkTarget } from "@Components/Link";
import type { IMap } from "@Components/Map";
import { Map, MapMarker } from "@Components/Map";
import * as stylex from "@stylexjs/stylex";

import { MapLink } from "../common/links/MapLink";
import WebpageLink from "../common/links/WebpageLink";
import type { IShoppingMallMapMarker } from "../map/ShoppingMallMapMarker";
import { ShoppingMallMapMarkerInfo } from "../map/ShoppingMallMapMarker";

import * as css from "./ShoppingMallTabDetails.module.css";

export interface IShoppingMallTabDetails extends IComponent {
    openingHoursTitle: string;
    additionalServicesTitle: string;
    websiteTitle: string;
    locationTitle: string;
    openingHours: IGroupedOpeningHours[];
    websiteLink: string;
    additionalServices: string[];
    map: IMap;
    showDirections: ILink;
}

interface IGroupedOpeningHours {
    serviceTypeDescription: string;
    serviceName: string;
    groupedDaysOpeningHours: IGroupedDayOpeningHours[];
}

interface IGroupedDayOpeningHours {
    daysOfWeekRange: string;

    // TODO: this two should be changed for DayOfWeek type after disable enum converter.
    firstDay: string;
    lastDay: string;

    openFromTo: string;
    openFrom: string;
    openTo: string;
    openToReason: string;
    isDayOff?: boolean;
    date?: Date;
    description: string;
}

export enum DayOfWeek {
    Sunday = 0,
    Monday = 1,
    Tuesday = 2,
    Wednesday = 3,
    Thursday = 4,
    Friday = 5,
    Saturday = 6,
}

export class ShoppingMallTabDetails extends Component<IShoppingMallTabDetails> {
    constructor(props: IShoppingMallTabDetails) {
        super(props);
        this.renderMarker = this.renderMarker.bind(this);
    }

    public shouldComponentUpdate(): boolean {
        return false;
    }

    public renderOpeningHours() {
        if (!this.props.openingHours || this.props.openingHours.length === 0) {
            return null;
        }

        const renderHours = (group: IGroupedDayOpeningHours[]) => {
            if (!group) {
                return;
            }

            const items = group.map((item: IGroupedDayOpeningHours, i: number) => (
                <div key={i} className={css.daysOfWeekRangeHours}>
                    <div
                        className={css.daysOfWeekRange}
                        dangerouslySetInnerHTML={{ __html: `${item.daysOfWeekRange}:` }}
                    />
                    <div className={css.hours} dangerouslySetInnerHTML={{ __html: item.openFromTo }} />
                </div>
            ));

            return items;
        };

        const openingContent = this.props.openingHours.map((item, i) => (
            <div key={i} className={css.service}>
                <div className={css.serviceTitle}>{item.serviceTypeDescription}</div>
                {renderHours(item.groupedDaysOpeningHours)}
            </div>
        ));

        return (
            <section className={css.openingHours}>
                <h3>{this.props.openingHoursTitle}:</h3>
                <div className={css.detailsContent}>{openingContent}</div>
            </section>
        );
    }

    public render() {
        const renderAdditionalServices = () => {
            if (!this.props.additionalServices || this.props.additionalServices.length === 0) {
                return null;
            }

            return (
                <div className={css.serviceContainer}>
                    {this.props.additionalServices.map((item, i) => (
                        <div key={i} className={css.additionalService}>
                            {" "}
                            {item}
                        </div>
                    ))}
                </div>
            );
        };

        const defaultPosition: google.maps.LatLngLiteral | undefined = this.props.map.initialData
            ? this.props.map.initialData[0].position
            : undefined;

        const initialData =
            (this.props.map.initialData && this.props.map.initialData.map((i) => ({ ...i }))) || [];

        return (
            <div {...defaultAttributes(this.props, css.shoppingMallTabDetailsComponent)}>
                <div className={css.detailsContent}>
                    <div className={css.detailsSubContent}>
                        {this.renderOpeningHours()}
                        <section className={css.additionalServices}>
                            <h3>{this.props.additionalServicesTitle}:</h3>
                            {renderAdditionalServices()}
                        </section>
                    </div>
                    <div className={css.detailsSubContent}>
                        <section>
                            <h3>{this.props.websiteTitle}:</h3>
                            <WebpageLink
                                href={this.props.websiteLink}
                                text={this.props.websiteLink}
                                styleX={styles.webLink}
                            />
                        </section>
                        <section className={css.location}>
                            <span className={css.anchor} id="Location" />
                            <h3>{this.props.locationTitle}:</h3>
                            <Map
                                {...this.props.map}
                                initialData={initialData}
                                className={css.map}
                                mapOptions={{
                                    ...Map.DefaultMapOptions,
                                    zoom: 15,
                                    center: defaultPosition,
                                }}
                                centerMarkersOnScreen={false}
                                defaultPosition={defaultPosition}
                                renderMarker={this.renderMarker}
                            />
                            <MapLink
                                className={css.mapLink}
                                {...this.props.showDirections}
                                hideMainLoading={true}
                                target={LinkTarget.Blank}
                                appearance={LinkAppearance.LightBlue}
                            />
                        </section>
                    </div>
                </div>
            </div>
        );
    }

    private renderMarker = (props: IShoppingMallMapMarker): JSX.Element => {
        const renderContent = (markerProps: IShoppingMallMapMarker) => (
            <ShoppingMallMapMarkerInfo {...markerProps.info} />
        );
        return <MapMarker {...props} renderInfoContent={renderContent} />;
    };
}

const styles = stylex.create({
    webLink: {
        fontSize: "1rem",
        fontWeight: 300,
        textTransform: "none",
    },
});
